import {OrderByCreateRequest, User, UserCreationRequest} from "../../shared/entities/Users/User";
import {CustomError} from "../../shared/errors/CustomErrors";
import {Option} from "../../shared/utilities/OptionT";
import {APIDao, Requester} from "../APIRequester";
import {Dealership, DealershipMinInfo} from "../../shared/entities/Users/Dealership";
import {RegistrationRequest} from "../../shared/entities/registrationRequest/RegistrationRequest";
import {COMPANY_API_PREFIX, REQUEST_REGISTRATION_API_PREFIX, USER_API_PREFIX} from "../../common/constants";

/**
 * UserRepository.
 *
 * Responsible for fetching data from backend
 * by callind APIDao and handling various data
 * coming from API
 *
 * Data is passed to UserService in a form of
 * an @type Option<T> type, where @param{Some: T} is either
 * a valid data, expected from the server,
 * or, in case of error, a default safe value,
 * for an object of array (kinda like default
 * values for types in Go),and @param{None: CustomError}
 * is either an error or undefined
 */
export class UserRepository<T> {
    private dao: Requester

    constructor(dao: Requester) {
        this.dao = dao
    }

    /**
     * getUserList.
     *
     * @returns {Promise<Option<Array<User>>>}
     *
     */
    public async getUserList(): Promise<Option<Array<User>>> {
        return await this.dao.getRequest<{ USERS: Array<User> }>(`${USER_API_PREFIX}/new-users-list`, {}).then(res => {
            const result: Option<Array<User>> = {
                Some: res.data?.USERS,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<Array<User>> = {
                Some: [],
                None: err
            }
            return result
        })
    }

    public async getUserListAll(params: {}): Promise<Option<{ users: Array<User>, count: number }>> {
        return await this.dao.getRequest<{
            users: Array<User>,
            count: number
        }>(`${USER_API_PREFIX}/all`, {params: {...params}}).then(res => {
            const result: Option<{ users: Array<User>, count: number }> = {
                Some: {
                    users: res?.data?.users,
                    count: res?.data?.count
                }
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<{ users: Array<User>, count: number }> = {
                //@ts-ignore
                Some: {
                    users: [],
                    count: 0
                },
                None: err
            }
            return result
        })
    }

    /**
     * Поиск пользователей для whitelist (логин или email, частичное совпадение через /user/all).
     */
    public async searchUsersForWhitelist(query: string, size = 30): Promise<Option<Array<User>>> {
        const q = query.trim();
        if (q.length < 2) {
            const empty: Option<Array<User>> = {Some: []};
            return empty;
        }
        const params = q.includes("@")
            ? {email: q, page: 0, size}
            : {login: q, page: 0, size};
        return await this.getUserListAll(params).then((res) => {
            if (res.None || !res.Some?.users) {
                const result: Option<Array<User>> = {Some: []};
                return result;
            }
            const result: Option<Array<User>> = {Some: res.Some.users};
            return result;
        });
    }

    /**
     * Загрузка пользователей по id (ответ {@code UserListDTO} с полем USERS).
     */
    public async getUsersByIds(ids: Array<number>): Promise<Option<Array<User>>> {
        if (!ids.length) {
            const empty: Option<Array<User>> = {Some: []};
            return empty;
        }
        const qs = ids.map((id) => `ids=${encodeURIComponent(String(id))}`).join("&");
        return await this.dao
            .getRequest<{ USERS?: Array<User> }>(`${USER_API_PREFIX}/list?${qs}`, {})
            .then((res) => {
                const raw = res?.data?.USERS;
                const arr = raw == null ? [] : Array.isArray(raw) ? raw : [...raw];
                const result: Option<Array<User>> = {Some: arr};
                return result;
            })
            .catch((err: CustomError) => {
                const result: Option<Array<User>> = {
                    Some: [],
                    None: err,
                };
                return result;
            });
    }

    public async getDealershipAll(params: {}): Promise<Option<{ dealerships: Array<Dealership>, count: number }>> {
        return await this.dao.getRequest<{
            dealerships: Array<Dealership>,
            count: number
        }>(`${USER_API_PREFIX}/dealership-all`, {params: {...params}}).then(res => {
            const result: Option<{ dealerships: Array<Dealership>, count: number }> = {
                Some: {
                    dealerships: res?.data?.dealerships,
                    count: res?.data?.count
                }
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<{ dealerships: Array<Dealership>, count: number }> = {
                //@ts-ignore
                Some: {
                    dealerships: [],
                    count: 0
                },
                None: err
            }
            return result
        })
    }

    public async getAll(data: T, body: T): Promise<Option<T>> {
        return await this.dao.postRequest<T>(`${USER_API_PREFIX}/users-all`, {
            params: {...data},
            data: {...body}
        })
            .then(res => {
                const result: Option<T> = {
                    Some: res?.data
                }
                return result
            })
    }

    public async getCompanyAll(data: T, body: T): Promise<Option<T>> {
        return await this.dao.postRequest<T>(`${COMPANY_API_PREFIX}/all`, {
            params: {...data},
            data: {...body}
        })
            .then(res => {
                const result: Option<T> = {
                    Some: res?.data
                }
                return result
            })
    }

    public async getCompany(id: {}): Promise<Option<T>> {
        return await this.dao.getRequest<T>(`${COMPANY_API_PREFIX}/${id}`, {params: {}}).then(res => {
            const result: Option<T> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<T> = {
                Some: {} as T,
                None: err
            }
            return result
        })
    }
    public async createCompany(data: any): Promise<Option<number>> {
        return await this.dao.postRequest<number>(`${COMPANY_API_PREFIX}/create`, {data}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }

    public async updateCompany(data: any): Promise<Option<number>> {
        return await this.dao.postRequest<number>(`${COMPANY_API_PREFIX}/update`, {data}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }

    public async getDealershipMinInfoAll(params: {}): Promise<Option<Array<DealershipMinInfo>>> {
        return await this.dao.getRequest<any>(`${USER_API_PREFIX}/dealership-min-info-all`, {params: {...params}}).then(res => {
            const result: Option<any> = {
                Some: res?.data
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                //@ts-ignore
                Some: [],
                None: err
            }
            return result
        })
    }

    public async getUser(login: {}): Promise<Option<User>> {
        return await this.dao.getRequest<User>(`${USER_API_PREFIX}/user`, {params: {login}}).then(res => {
            const result: Option<User> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<User> = {
                Some: {} as User,
                None: err
            }
            return result
        })
    }


    public async getDealership(params: {}): Promise<Option<Dealership>> {
        return await this.dao.getRequest<Dealership>(`${USER_API_PREFIX}/dealership`, {params: {...params}}).then(res => {
            const result: Option<Dealership> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<Dealership> = {
                Some: {} as Dealership,
                None: err
            }
            return result
        })
    }


    /**
     * createUser.
     *
     * @param {any} data
     * @returns {Promise<Option<number>>}
     */
    public async createUser(data: any): Promise<Option<number>> {
        return await this.dao.postRequest<number>(`${USER_API_PREFIX}/create-user`, {data}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }

    public async restorePassword(data: any): Promise<Option<number>> {
        return await this.dao.postRequest<number>(`${USER_API_PREFIX}/restore-password`, {data}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }

    public async changePassword(data: any): Promise<Option<number>> {
        return await this.dao.postRequest<number>(`${USER_API_PREFIX}/change-password`, {data}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }
    public async getLoginCode(code: any): Promise<Option<number>> {
        return await this.dao.getRequest<number>(`${USER_API_PREFIX}/get-login-code`, {params: {code}}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }

    /**
     * updateUser
     *
     * @param {any} data
     * @returns {Promise<Option<number>>}
     */
    public async updateUser(data: any): Promise<Option<number>> {
        return await this.dao.postRequest<number>(`${USER_API_PREFIX}/update-user`, {data}).then(res => {
            const result: Option<number> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<number> = {
                Some: 0,
                None: err
            }
            return result
        })
    }

    public async deleteUser(id: number) {
        await this.dao.putRequest<User>(`${USER_API_PREFIX}/delete/${id}`, {});
    }

    public async deleteCompany(id: number) {
        await this.dao.putRequest<User>(`${COMPANY_API_PREFIX}/delete/${id}`, {});
    }

    public async deleteDealership(id: number) {
        await this.dao.putRequest<User>(`${USER_API_PREFIX}/delete-dealership/${id}`, {});
    }

    public async companyRegistered(id: number) {
        return await this.dao.postRequest<{}>(`${USER_API_PREFIX}/company-registered/${id}`, {}).then(res => {
            const result: Option<any> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: [],
                None: err
            }
            return result
        })
    }

    public async inviteCreate(id: number) {
        return await this.dao.postRequest<{}>(`${USER_API_PREFIX}/invite-create/${id}`, {}).then(res => {
            const result: Option<any> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: [],
                None: err
            }
            return result
        })
    }

    public async setNewPassword(data: any): Promise<Option<any>> {
        return await this.dao.postRequest<any>(`${USER_API_PREFIX}/set-new-password`, {data}).then(res => {
            const result: Option<any> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: [],
                None: err
            }
            return result
        })
    }

    public async userCreationRequest(data: UserCreationRequest): Promise<Option<any>> {
        return await this.dao.postRequest<any>(`${REQUEST_REGISTRATION_API_PREFIX}/create`, {data}).then(res => {
            const result: Option<any> = {
                Some: res.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: {} as -1,
                None: err
            }
            return result
        })
    }

    public async orderByCatalogCreate(data: OrderByCreateRequest): Promise<Option<any>> {
        return await this.dao.postRequest<any>("mail/order-by-create", {data}).then(res => {
            const result: Option<any> = {
                Some: res.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: {} as -1,
                None: err
            }
            return result
        })
    }

    public async getRequestRegistrationAll(params: {}): Promise<Option<{
        registrationUsers: Array<RegistrationRequest>,
        count: number
    }>> {
        return await this.dao.getRequest<{
            registrationUsers: Array<RegistrationRequest>,
            count: number
        }>(`${REQUEST_REGISTRATION_API_PREFIX}/all`, {params: {...params}}).then(res => {
            const result: Option<{ registrationUsers: Array<RegistrationRequest>, count: number }> = {
                Some: {
                    registrationUsers: res?.data?.registrationUsers,
                    count: res?.data?.count
                }
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<{ registrationUsers: Array<RegistrationRequest>, count: number }> = {
                //@ts-ignore
                Some: {
                    registrationUsers: [],
                    count: 0
                },
                None: err
            }
            return result
        })
    }

    public async confirmCompleted(id: number): Promise<Option<any>> {
        return await this.dao.postRequest<{}>(`${REQUEST_REGISTRATION_API_PREFIX}/confirm-completed/${id}`, {}).then(res => {
            const result: Option<any> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: [],
                None: err
            }
            return result
        })
    }

    public async dealershipFeeState(data: any): Promise<Option<any>> {
        return await this.dao.postRequest<{}>(`${USER_API_PREFIX}/dealership-fee-state`, {data}).then(res => {
            const result: Option<any> = {
                Some: res?.data,
            }
            return result
        }).catch((err: CustomError) => {
            const result: Option<any> = {
                Some: [],
                None: err
            }
            return result
        })
    }
}

export const UserRepositoryInstance = new UserRepository(APIDao) 
